import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { motion } from 'motion/react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { generateYearlySummary } from '@/lib/mortgage'
import type { MortgageInput, OverpaymentComparison, InstallmentType } from '@/lib/mortgage'
import { formatPLN } from '@/lib/constants'

interface PaymentChartProps {
  input: MortgageInput
  overpaymentComparison?: OverpaymentComparison | null
  installmentType?: InstallmentType
}

export function PaymentChart({ input, overpaymentComparison, installmentType = 'equal' }: PaymentChartProps) {
  const { t } = useTranslation()
  const hasOverpayment = overpaymentComparison && overpaymentComparison.monthsSaved > 0

  const data = useMemo(() => {
    const baseData = generateYearlySummary(input, installmentType)
    const maxYears = baseData.length

    if (!hasOverpayment || !overpaymentComparison) {
      // Single line: just remaining balance
      return baseData.map((row) => ({
        year: row.year,
        balance: Math.round(row.remainingBalance),
      }))
    }

    // Two lines: base vs overpayment remaining balance
    const result = []
    for (let i = 0; i < maxYears; i++) {
      const base = baseData[i]
      const over = overpaymentComparison.overpaymentYearlySummary[i]
      result.push({
        year: base.year,
        balance: Math.round(base.remainingBalance),
        balanceOver: over ? Math.round(over.remainingBalance) : 0,
      })
    }
    return result
  }, [input, hasOverpayment, overpaymentComparison])

  // Show every Nth year on X-axis to prevent label overlap
  const totalYears = data.length
  const tickInterval = totalYears > 25 ? 4 : totalYears > 15 ? 2 : 1

  const xAxisTicks = useMemo(() => {
    if (totalYears <= 15) return undefined // let Recharts handle it
    const ticks: number[] = [1] // always show year 1
    for (let y = tickInterval + 1; y <= totalYears; y += tickInterval) {
      ticks.push(y)
    }
    if (ticks[ticks.length - 1] !== totalYears) {
      ticks.push(totalYears) // always show last year
    }
    return ticks
  }, [totalYears, tickInterval])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Card className="overflow-hidden">
        <CardContent className="py-5 px-5">
          <h2 className="text-lg font-bold text-foreground mb-4">
            {t('chart.titleComparison')}
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 15 }}>
                <defs>
                  <linearGradient id="balanceGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={hasOverpayment ? 0.15 : 0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="overBalanceGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.5} />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  ticks={xAxisTicks}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <RechartsTooltip
                  formatter={(value, name) => [
                    formatPLN(Number(value)),
                    String(name) === 'balanceOver'
                      ? t('chart.balanceOver')
                      : hasOverpayment
                        ? t('chart.balanceBase')
                        : t('chart.balance'),
                  ]}
                  labelFormatter={(label) => `${t('chart.year')} ${label}`}
                  contentStyle={{
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 10px 25px -5px rgb(0 0 0 / 0.1)',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                  }}
                />
                <Legend
                  formatter={(value) =>
                    value === 'balanceOver'
                      ? t('chart.balanceOver')
                      : hasOverpayment
                        ? t('chart.balanceBase')
                        : t('chart.balance')
                  }
                />
                <Area
                  type="monotone"
                  dataKey="balance"
                  stroke="#6366f1"
                  strokeWidth={2}
                  strokeDasharray={hasOverpayment ? '6 3' : undefined}
                  fill="url(#balanceGrad)"
                  animationDuration={1000}
                />
                {hasOverpayment && (
                  <Area
                    type="monotone"
                    dataKey="balanceOver"
                    stroke="#14b8a6"
                    strokeWidth={2.5}
                    fill="url(#overBalanceGrad)"
                    animationDuration={800}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}
